import React, { useState } from 'react';
import {
    formatStatLine,
    formatPoints,
    formatPointsDelta,
    STAT_MODE_PROJECTED,
} from '../../utils/statLineFormat';
import './PlayerProjectionSummary.css';

/**
 * PROJECTED score breakdown for the PlayerPage GEAR tab.
 *
 * Presents the SAME visual format as the PTS tab's `CollapsibleScoreBreakdown`
 * — a summary bar, a yellow-triangle "Stats" section and a green-triangle
 * "Gear Adj" section — but populated with PROJECTIONS instead of actual
 * results. Keeping the two formats identical is a deliberate product
 * requirement: a manager reading a projection should be reading the same
 * layout they read after the games.
 *
 * WHY THIS IS A SEPARATE COMPONENT rather than a prop on the PTS one:
 * `CollapsibleScoreBreakdown` consumes the ACTUAL scoring breakdown shape
 * (nested `stat_line` / `stat_line_modified` objects emitted by the scoring
 * engine). The projection adapter emits a flat, much smaller shape. Forcing
 * one component to serve both would mean a pile of shape-sniffing branches;
 * the shared *formatting* lives in `statLineFormat.js`, which is the part that
 * actually must not drift.
 *
 * ⭐ THE GREEN SECTION NOW RENDERS A REAL GEAR-ADJUSTED STAT LINE.
 *
 * It is `summary.gearedStats` — the tier-average line with the player's gear
 * applied — computed by the REAL Python scoring engine server-side and passed
 * through unchanged. With Passed Life equipped (`mult +0.75x pass yards`) the
 * yellow line's `249.2 PASS YD` becomes `436.1 PASS YD` in green.
 *
 * ⚠️ NO GEAR RULE IS EVER APPLIED IN THIS COMPONENT, and none may be added.
 * It renders two stat lines the server produced and one subtraction the
 * adapter made. The earlier V1 deliberately showed no green line at all rather
 * than port the Python gear rules into the browser; that prohibition still
 * stands — what changed is that the server now supplies the answer.
 *
 * ⚠️ BOTH LINES GO THROUGH THE SAME FORMATTER CALL. `formatStatLine(...,
 * STAT_MODE_PROJECTED)`, same mode, same field-name duality handling
 * (`passing_attempts`/`attempts`, `fumbles_lost`/`fumbles`). Identical
 * formatting is the whole point — a user compares the two lines directly, so
 * any divergence means the formatter was bypassed.
 */
function PlayerProjectionSummary({ summary }) {
    const [expandedSections, setExpandedSections] = useState({
        stats: true,   // Default OPEN, matching the PTS tab
        gearAdj: true,
    });

    if (!summary) {
        throw new Error(
            'PlayerProjectionSummary rendered without a summary. Expected the object ' +
            'returned by buildProjectionSummary() — pass its `available: false` result ' +
            'rather than null so the unavailable state can be explained to the user.'
        );
    }

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    // UNAVAILABLE STATE — shown honestly rather than as a row of zeroes.
    //
    // The opponent case is titled differently on purpose. "Projection
    // unavailable" reads like something went wrong, but viewing another
    // manager's player is a normal, expected action with a deliberate product
    // boundary behind it: the rank endpoint is owner-only. Naming that keeps
    // the screen truthful without implying a fault the user should report.
    if (!summary.available) {
        const isOpponentLimitation = summary.unavailableBecauseOpponentView === true;

        return (
            <div className="projection-summary projection-summary--unavailable">
                <div className="projection-unavailable-title">
                    {isOpponentLimitation
                        ? 'Projections are for your team only'
                        : 'Projection unavailable'}
                </div>
                <div className="projection-unavailable-reason">{summary.reason}</div>
                <div className="projection-unavailable-hint">
                    {isOpponentLimitation
                        ? 'Their equipped gear is still listed below.'
                        : 'You can still equip and unequip gear normally.'}
                </div>
            </div>
        );
    }

    // ⚠️ PROJECTED MODE IS REQUIRED HERE. These are `PlayerTierAverages`, so
    // sub-1.0 rates (`receiving_tds: 0.51`) are real, meaningful figures. The
    // formatter defaults to ACTUAL mode — which floors, correctly, for the PTS
    // tab — and under that default every fractional touchdown rate would floor
    // to 0, be dropped by the zero-filter, and silently vanish from the line.
    const statLine = formatStatLine(summary.estimatedStats, STAT_MODE_PROJECTED);

    /**
     * The green line. SAME formatter, SAME mode as the yellow line above —
     * that is deliberate and load-bearing, not incidental reuse.
     */
    const gearedStatLine = formatStatLine(summary.gearedStats, STAT_MODE_PROJECTED);

    const hasGear = summary.gearItems.length > 0;

    /**
     * ⚠️ WHICH OF THE THREE STATES IS THIS? (design §6)
     *
     * The green section must ALWAYS say WHY it looks the way it does. Left
     * unexplained, a `+6.40pts` adjustment sitting above a stat line identical
     * to the yellow one reads as a bug — and it is not. Only
     * `RAW_STAT_MODIFICATION` gear moves stat VALUES;
     * `BASE_POINTS_MODIFICATION`, `MULTIPLIER_MODIFICATION`, `THRESHOLD_BONUS`
     * and `CUSTOM_HARDCODED` move POINTS with the stat line untouched.
     *
     *   1. statsChanged  — the line really moved (the Passed Life case).
     *   2. pointsOnly    — scoring changed, the line did not. EXPLAINED, and
     *                      the identical line is repeated DELIBERATELY: hiding
     *                      it would make the section look broken, and a user
     *                      comparing yellow to green should be able to SEE
     *                      that they match.
     *   3. noEffect      — nothing changed at all. Renders `+0.00pts`, never
     *                      an em dash: we checked, and the answer is zero.
     *
     * `statLineChanged` comes from the server, decided on UNROUNDED values.
     * It is never re-derived here — comparing two rounded lines client-side
     * would silently lose a change smaller than the displayed precision.
     */
    const statsChanged = summary.statLineChanged === true;
    // `gearPoints` is null when nothing in a non-empty loadout could be
    // analysed. That is "unknown", which is neither of the two "we checked"
    // states below — it must not be described as "no effect".
    const gearPointsKnown = summary.gearPoints != null;
    const pointsOnly = !statsChanged && gearPointsKnown && summary.gearPoints !== 0;
    const noEffect = !statsChanged && gearPointsKnown && summary.gearPoints === 0;

    return (
        <div className="projection-summary">
            {/* Provenance warning. Only rendered while the tier data is mocked;
                it disappears automatically once the fixture's isMock flips. */}
            {summary.isMock && (
                <div className="projection-mock-banner" role="status">
                    <strong>PLACEHOLDER DATA</strong> — per-player estimates are stand-in
                    values pending the 2025 tier analysis. Gear point impacts are real.
                </div>
            )}

            {/* Summary bar — identical format to the PTS tab's */}
            <div className="projection-summary-bar">
                <span className="projection-summary-item">
                    <strong>Base:</strong> {formatPoints(summary.basePoints)}
                </span>
                <span className="projection-summary-separator">|</span>
                <span className="projection-summary-item">
                    <strong>Gear:</strong> {formatPointsDelta(summary.gearPoints)}
                </span>
                <span className="projection-summary-separator">|</span>
                <span className="projection-summary-item">
                    <strong>Total:</strong> {formatPoints(summary.totalPoints)}
                </span>
            </div>

            {summary.partialScoringInputs?.length > 0 && (
                <div className="projection-unknown-note" role="status">
                    * Partial points estimate: Tank01 reports projected two-point
                    conversions without identifying passing, rushing, or receiving.
                    Those scoring contributions are excluded from Base, Gear, and
                    Total. The stat line still comes from the actual NFL projection.
                </div>
            )}

            <div className="projection-sections">
                {/* ================= STATS — YELLOW TRIANGLE ================= */}
                <div className="projection-section">
                    {!expandedSections.stats ? (
                        <button
                            type="button"
                            className="projection-triangle-button"
                            onClick={() => toggleSection('stats')}
                            aria-expanded="false"
                            aria-label="Expand projected stats"
                        >
                            <span className="projection-triangle projection-triangle--stats">▶</span>
                        </button>
                    ) : (
                        <div className="projection-section-open">
                            <button
                                type="button"
                                className="projection-triangle-button"
                                onClick={() => toggleSection('stats')}
                                aria-expanded="true"
                                aria-label="Collapse projected stats"
                            >
                                <span className="projection-triangle projection-triangle--stats">▼</span>
                            </button>

                            <div className="projection-section-body">
                                <div className="projection-section-heading">Stats:</div>

                                <div className="projection-stat-line">
                                    {statLine || '(no projected stats)'}
                                </div>

                                <div className="projection-points-line">
                                    Points: {formatPoints(summary.basePoints)}
                                </div>

                                <div className="projection-detail-list">
                                    <div>• Position: {summary.position}</div>
                                    {summary.source?.year != null && (
                                        <div>
                                            • Source: {summary.source.year} week{' '}
                                            {summary.source.week} NFL projections
                                            {summary.source.provider && ` (${summary.source.provider})`}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ================ GEAR ADJ — GREEN TRIANGLE ================ */}
                <div className="projection-section">
                    {!expandedSections.gearAdj ? (
                        <button
                            type="button"
                            className="projection-triangle-button"
                            onClick={() => toggleSection('gearAdj')}
                            aria-expanded="false"
                            aria-label="Expand projected gear adjustment"
                        >
                            <span className="projection-triangle projection-triangle--gear">▶</span>
                        </button>
                    ) : (
                        <div className="projection-section-open">
                            <button
                                type="button"
                                className="projection-triangle-button"
                                onClick={() => toggleSection('gearAdj')}
                                aria-expanded="true"
                                aria-label="Collapse projected gear adjustment"
                            >
                                <span className="projection-triangle projection-triangle--gear">▼</span>
                            </button>

                            <div className="projection-section-body">
                                <div className="projection-section-heading">Gear Adj:</div>

                                {/* ⚠️ NO GEAR EQUIPPED IS A KNOWN ZERO, NOT A
                                    MISSING LINE. There is nothing to compare,
                                    so no green stat line is drawn — but the
                                    points below still read "+0.00pts", never
                                    an em dash. Wearing nothing is a fully
                                    understood state, not an unknown one. */}
                                {!hasGear ? (
                                    <div className="projection-stat-line">
                                        (no gear equipped)
                                    </div>
                                ) : (
                                    <>
                                        {/* ⭐ THE GEAR-ADJUSTED PROJECTED STAT LINE.
                                            Server-computed; identical format to the
                                            yellow line above, so the two can be read
                                            against each other directly. */}
                                        <div className="projection-stat-line">
                                            {gearedStatLine || '(no projected stats)'}
                                        </div>

                                        {/* THE STATE EXPLANATION (design §6). An
                                            identical green line is deliberate, not a
                                            rendering failure — say so, or it reads as
                                            a bug. */}
                                        {pointsOnly && (
                                            <div className="projection-state-note">
                                                Your gear changes scoring, not the projected
                                                stat line.
                                            </div>
                                        )}
                                        {noEffect && (
                                            <div className="projection-state-note">
                                                Your equipped gear is projected to have no
                                                effect for this player.
                                            </div>
                                        )}
                                    </>
                                )}

                                {/* ⚠️ THE SECTION TOTAL COMES FROM `Total − Base`,
                                    NEVER FROM SUMMING THE ROWS BELOW. Per-item
                                    deltas are LEAVE-ONE-OUT marginal contributions,
                                    so when items interact (two multipliers on one
                                    stat, a threshold that only trips because another
                                    item added yards) they legitimately do not sum to
                                    this figure. That is truthful attribution, not an
                                    arithmetic error — do not "fix" it by summing. */}
                                <div className="projection-points-line">
                                    Points: {formatPointsDelta(summary.gearPoints)}
                                </div>

                                {hasGear && (
                                    <div className="projection-detail-list">
                                        {summary.gearItems.map((item) => (
                                            <div key={item.id} className="projection-gear-entry">
                                                <div className="projection-gear-name">
                                                    • {item.name}
                                                    {' '}
                                                    {/* `null` → "—" (not analysed);
                                                        `0` → "+0.00pts" (analysed, inert).
                                                        `formatPointsDelta()` preserves the
                                                        distinction; never coerce it here. */}
                                                    <span className="projection-gear-impact">
                                                        {formatPointsDelta(item.impact)}
                                                    </span>
                                                </div>

                                                {/* WHAT THIS ITEM ACTUALLY DID, from the
                                                    server's own attribution — so a user can
                                                    tell which item moved the green line and
                                                    which only moved the points. */}
                                                {item.statEffects?.length > 0 && (
                                                    <div className="projection-gear-effects">
                                                        {item.statEffects.map((effect, index) => (
                                                            <div
                                                                key={`${effect.stat}-${index}`}
                                                                className="projection-gear-effect"
                                                            >
                                                                {effect.stat}: {effect.before} →{' '}
                                                                {effect.after}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* An item with point-level effects and NO
                                                    stat effects gets the explicit phrase, so
                                                    its unchanged contribution to the green
                                                    line is accounted for rather than looking
                                                    like an omission. */}
                                                {item.projectionKnown
                                                    && item.statEffects?.length === 0
                                                    && item.nonStatEffects?.length > 0 && (
                                                    <div className="projection-gear-effects">
                                                        affects points, not stats
                                                    </div>
                                                )}

                                                {item.description && (
                                                    <div className="projection-gear-description">
                                                        {item.description}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Partial-coverage disclosure. Without this the total would
                                    silently understate the loadout's impact. */}
                                {summary.unknownGearCount > 0 && (
                                    <div className="projection-unknown-note">
                                        {summary.unknownGearCount} equipped item
                                        {summary.unknownGearCount === 1 ? ' has' : 's have'} no
                                        projection data and {summary.unknownGearCount === 1 ? 'is' : 'are'}
                                        {' '}not included in the total above.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default PlayerProjectionSummary;
